import {
	AuthInfoWithCnf,
	ClientInfoType,
	ctxConsoleLog,
	ENVIRONMENT,
	knownAADClientInfo,
} from '@citrite/msal-browser';
import { versionInfo } from '@citrite/workspace-ui-platform';
import { Environment } from 'Environment/Environment';
import { getFromLocalStorage } from 'javascript/sf/Storage';
import { AADSSOICAContents } from 'Workspace/BrowserExtension/aadSSOSpecificIcaFileParser';
import { lastUserDetailsKey } from 'Workspace/UserContext/constants';
import { UserDetailsCacheEntry } from 'Workspace/UserContext/UserContextLoader';

//This feild is sent to Chrome app from the Native client
// So that they can use if for the MSAL login hint
// Selecting a name which indicates WSP usage instead MSAL specific
export const userAuthAliasKeyForICAData = 'UserAuthAlias';

export const incompleteClientInfoError = (correlationId: string) => {
	const innerErrorObject = {
		errorCode: 'invalid_request',
		errorMessage:
			'Invalid or Incomplete client info obtained from Citrix Workspace Native App.',
		name: 'CtxLocalError',
		correlationId,
	};

	return {
		error: JSON.stringify(innerErrorObject),
		correlationId,
	};
};

export function wsuiAuthEnvironment() {
	const wsuiInfo = versionInfo.get();
	return wsuiInfo.environment === 'release' && wsuiInfo.release !== 'master'
		? ENVIRONMENT.PROD
		: ENVIRONMENT.NON_PROD;
}

/*
	This is required since native clients will pass back verbatim , the new AADSSO from the ICA file , 
	We need to convert these two field names to the correct field names in the AuthInfo object. 
	AzureSSOnResourceAppId   ---> WSUI has to convert to resourceAppId
	AzureSSOnAuthorizeUrl    ---> WSUI has to convert to authority

	The following other two fields from the ICA file, the CWA Native apps will convert and send to WSUI
	to preserve backward compatibility: 

	AzureSSOnClientId     ---> The CWA Native apps will convert to -----> clientId  
	AzureSSOnRedirectUri  ---> The CWA Native apps will convert to -----> redirectUri
*/
export function parseJsonFromNativeToAuthInfo(jsonPayload: string): AuthInfoWithCnf {
	const rawParsedAuthInfo: AuthInfoWithCnf &
		Pick<AADSSOICAContents, 'AzureSSOnResourceAppId' | 'AzureSSOnAuthorizeUrl'> =
		JSON.parse(jsonPayload);

	return {
		...rawParsedAuthInfo,
		authority: rawParsedAuthInfo.AzureSSOnAuthorizeUrl,
		resourceAppId: rawParsedAuthInfo.AzureSSOnResourceAppId,
	};
}

export function getUpnOrAuthAliasForCurrentUser(): string | undefined {
	try {
		const lastLoggedUserFromStorage =
			getFromLocalStorage<UserDetailsCacheEntry>(lastUserDetailsKey);
		return (
			lastLoggedUserFromStorage?.upn || lastLoggedUserFromStorage?.authAlias || undefined
		); // we don't want falsy strings like empty strings either
	} catch (error) {
		/** Ignore errors. Empty loginHint is fine */
	}

	return undefined;
}

const requiredClientInfoFields: (keyof ClientInfoType)[] = [
	'clientId',
	'authority',
	'redirectUri',
	'resourceAppId',
];

export function validateClientInfo(clientInfo: ClientInfoType): boolean {
	if (!clientInfo) {
		return false;
	}

	// All fields are required
	const hasMissingFields = requiredClientInfoFields.some(field => {
		const value = clientInfo[field];
		return typeof value !== 'string' || value.trim() === '';
	});

	//All required fields are non empty and clientID is not Microsoft Id
	return (
		!hasMissingFields &&
		clientInfo?.clientId !== knownAADClientInfo.MicrosoftSample.clientId
	);
}

const getLowerCaseOriginWithPath = (urlString: string): string => {
	const url = new URL(urlString);
	return (url.origin + url.pathname).toLowerCase();
};

/*
	 For the MSAL Library to be able to internally read the #code sent to the redirect URI,
	 the redirect URI origin must match the store URL origin.
	 This function checks if the redirect URI origin matches the store URL origin.
*/
export function isStoreUrlAndRedirectUriOriginMatch(redirectUri: string): boolean {
	try {
		const lowerCaseStoreOriginWithPath = getLowerCaseOriginWithPath(window.location.href);
		const lowerCaseRedirectUri = getLowerCaseOriginWithPath(redirectUri);

		// We use startsWith to allow for cases where store URL has additional path segments
		// due to ui navigation in sub folders.
		const ret = lowerCaseStoreOriginWithPath.startsWith(lowerCaseRedirectUri);
		ctxConsoleLog(
			`isStoreUrlAndRedirectUriOriginMatch:= ${ret}`,
			ret ? 'Matched. Using Customers Redirect' : 'Not Matched. Using CTX Redirect'
		);
		return ret;
	} catch (e) {
		ctxConsoleLog('isStoreUrlAndRedirectUriOriginMatch: Error parsing URLs', e);
		return false;
	}
}

/**
 *
 * @param redirectUriFromIca
 * @param env
 * @returns boolean
 *
 * This special URL modification is applicable only for
 * 1) Mobile Native clients : since they always want custom scheme redirection
 * 2) Mac\Linux: when they want system browser which relies on custom scheme redirection
 *
 * Note : Both the above clients explicitly advertise
 * this requirement via the bridge capabilities flag: {NativeCapabilities.supportsInteractiveAuthOnly})
 
 * Until now, we never sent custom scheme redirect URIs directly to
 * Microsoft while obtaining the #auth code.
 *
 * For Citrix Managed Entra ClientIDs, we always send a citrix hosted "https://"
 * redirection uri to microsoft while obtaining the #auth code.
 * This Redirect Uri points to the Citrix CDN javascript page
 * which intelligently knows how to redirect back to the StoreWeb after Microsoft sends
 * #auth code to that page. This page explicitly invokes the "ctxaadsso://citrix.aad.redirect://"
 * if the native client had set the "supportsInteractiveAuthOnly" flag and the "performAuthorizationV2" function.
 *
 * For Customer registered Entra ClientIDs:
 *
 * 1) The https redirect page is always customers domain specific it already knows
 * how to get back to its own domain after receiving the #authcode from Microsoft
 * (since its a single tenant id instead of multi tenant, the store domain is always fixed
 * and equal to the redirect URI itself).
 *
 * 2) If we want to continue supporting the custom scheme redirection flow,
 * we need to send the custom scheme redirect URI directly to Microsoft while
 * obtaining the #auth code. We "CANNOT" use the https url here since the native clients
 * open this page on a separate browser instance which does not have any context of the
 * logged the StoreWeb URL. Even if it had the context, it wont open via Netscaler Gateway without
 * authentication without the the complex configuration required for this page to bypass the
 * Netscaler Gateway (a pain point already raised by customers with
 * the Citrix hosted page).
 *
 * We will send the custom scheme redirect URI to Microsoft only if all of the following
 * conditions are met:
 *
 * 1) The Native client supports interactive auth only (mobile clients always do)
 * 2) The Native client supports "performAuthorizationV2" function
 * 3) The Native client supports "skipGatewaySupportedByAjax" function
 * 4) The redirect URI from ICA file is NOT Citrix Managed HTTPS (i.e it is customer registered HTTPS)
 *
 * If any of the above conditions are NOT met, we will continue sending the https redirect URI to Microsoft
 * while obtaining the #auth code.
 */
export function shouldUseCustomSchemeForCustomerManagedClientId(
	redirectUriFromIca: string,
	env: Environment
): boolean {
	const msg = 'CustomScheme Redirect NOT Required :';
	if (env.supportsInteractiveAuthOnly !== true) {
		ctxConsoleLog(msg, 'Client Does not support interactive auth;');
		return false;
	}

	if (env?.nativeCapabilities?.functions?.includes('performAuthorizationV2') !== true) {
		ctxConsoleLog(msg, 'Client Does not support performAuthorizationV2');
		return false;
	}

	if (env?.nativeCapabilities?.skipGatewaySupportedByAjax !== true) {
		ctxConsoleLog(msg, 'Client Does not support skipGatewaySupportedByAjax');
		return false;
	}

	// This is the critical check if the redirect URI is Citrix managed HTTPS
	// or customer registered HTTPS for single tenant client IDs.
	if (!isStoreUrlAndRedirectUriOriginMatch(redirectUriFromIca)) {
		ctxConsoleLog(msg, 'Redirect URI from ICA is Citrix Managed HTTPS');
		return false;
	}

	ctxConsoleLog('CustomScheme Redirect Required for #authcode: All conditions met');
	return true;
}
